/**
 * ANSWERING WHAT THE WORK ASKED FOR.
 *
 * This is the one governed resume gate in the product, and the contract is
 * narrow on purpose: an answer CITES records already enclosed in the work
 * (`evidenceReferenceIds`, at least one) and may carry a note. Enclosing a
 * record is not an answer and resumes nothing — a person decides whether what
 * arrived actually satisfies what was asked, and only then does the same task
 * continue.
 *
 * So the control is exactly that: enclose the record if it is not here yet,
 * then say which records answer the question. There is no "I don't have this",
 * because the engine has no such response to record.
 *
 * Only records that were ADMITTED and carry an `evidenceReferenceId` can be
 * cited — a refused record has nothing to cite. That is a property of the
 * projection, read from it, not a rule this screen applies to the work.
 */
import { useState } from "react";
import {
  WorkActionResponse, type EnclosedRecord, type OpenRequest, type RecordKind, type WorkFile,
} from "@leveri/contracts";
import { Button, Field, Notice, Reading, RecordName, RuledList } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";
import { EncloseRecord } from "./enclose.js";
import { useKeyring } from "./keys.js";

export interface AnswerRequestProps {
  workId: string;
  request: OpenRequest;
  records: readonly EnclosedRecord[];
  /** Every kind a record can be, named by the server, for the enclose control. */
  kinds: readonly RecordKind[];
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}

export function AnswerRequest({ workId, request, records, kinds, onWork, onOutcome }: AnswerRequestProps) {
  const [cited, setCited] = useState<readonly string[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const keyFor = useKeyring();

  const citable = records.filter((record) => record.outcome === "ADMITTED" && record.evidenceReferenceId !== null);

  function toggle(referenceId: string) {
    setCited((current) =>
      current.includes(referenceId) ? current.filter((id) => id !== referenceId) : [...current, referenceId]
    );
  }

  async function answer() {
    if (busy || cited.length === 0) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await call("POST", `/api/work/${workId}/requests/${request.requirementId}`, WorkActionResponse, {
        evidenceReferenceIds: cited,
        ...(note.trim() ? { note: note.trim() } : {}),
        // The act is "answer THIS request with THESE records". Citing a
        // different set is a different act; pressing the button twice on the
        // same set is one.
        submissionKey: keyFor(`answer:${request.requirementId}:${[...cited].sort().join(",")}`),
      });
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-rule-soft pt-4">
      <EncloseRecord workId={workId} kinds={kinds} onEnclosed={onWork} />

      <div className="mt-5">
        <div className="text-sm font-medium text-ink-soft">Which records answer this?</div>
        {citable.length === 0 ? (
          <p className="mt-1.5 text-[13px] text-stone">
            Nothing in this work can be cited yet. Enclose the record above and it will appear here.
          </p>
        ) : (
          <RuledList className="mt-1.5">
            {citable.map((record) => (
              <label key={record.evidenceReferenceId} className="flex cursor-pointer items-baseline gap-3 py-2.5 hover:bg-band">
                <input
                  type="checkbox"
                  className="accent-forest"
                  disabled={busy}
                  checked={cited.includes(record.evidenceReferenceId as string)}
                  onChange={() => toggle(record.evidenceReferenceId as string)}
                />
                <span className="min-w-0">
                  <RecordName className="block truncate">{record.filename}</RecordName>
                  {record.roleLabel && <span className="text-[13px] text-stone">{record.roleLabel}</span>}
                </span>
              </label>
            ))}
          </RuledList>
        )}
      </div>

      <div className="mt-4">
        <Field label="Note" hint="Optional. It is filed with the answer.">
          <textarea
            rows={2}
            value={note}
            disabled={busy}
            onChange={(event) => setNote(event.target.value)}
            className="w-full rounded-control border border-line-strong bg-canvas px-3 py-2 text-sm text-ink focus:border-brand"
          />
        </Field>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3.5">
        <Button disabled={busy || cited.length === 0} onClick={() => void answer()}>Answer with these records</Button>
      </div>
      {busy && <div className="mt-3"><Reading>Answering, and resuming the work…</Reading></div>}
      {failure && <div className="mt-3"><Notice>{failure}</Notice></div>}
    </div>
  );
}
